import React, { useEffect, useRef, useState } from 'react'

import { $isStickyCTAVisible } from '@/stores/ui/stores'
import type { CMSProgram } from '@/types/sanity'
import { ResponsiveImage } from '../Shared/ResponsiveImage'

import { Icon } from '../Icon'

import './StickyCTA.scss'

type NutritionProgramsStickyCTAProps = {
  program: CMSProgram
}

export const NutritionProgramsStickyCTA: React.FC<
  NutritionProgramsStickyCTAProps
> = ({ program }) => {
  const [isBuyingPanelOutOfView, setIsBuyingPanelOutOfView] = useState(false)
  const visibleButtonRef = useRef<HTMLButtonElement | null>(null)

  const { stickyCTA } = program

  useEffect(() => {
    const checkIntersection = () => {
      const sectionId = stickyCTA?.moduleAfterWhichToShow?._id
      const targetSection = sectionId
        ? document.getElementById(sectionId)
        : document.querySelector('section')

      if (targetSection) {
        const targetSectionRect = targetSection.getBoundingClientRect()

        const isTargetSectionOutOfView = targetSectionRect.bottom < 0

        setIsBuyingPanelOutOfView(isTargetSectionOutOfView)

        if (isTargetSectionOutOfView) {
          document.body.classList.add('has-sticky')
        } else {
          document.body.classList.remove('has-sticky')
        }
      }
    }

    checkIntersection()
    window.addEventListener('scroll', checkIntersection)

    return () => {
      window.removeEventListener('scroll', checkIntersection)
    }
  }, [])

  const getVisibleButton = (buttons: NodeListOf<HTMLButtonElement>) => {
    buttons.forEach((button) => {
      if (button.style.display !== 'none') {
        visibleButtonRef.current = button
      }
    })
  }

  useEffect(() => {
    const buttons = document.querySelectorAll(
      '.program-panel-submit-action',
    ) as NodeListOf<HTMLButtonElement>
    getVisibleButton(buttons)
  }, [])

  const addToCart = () => {
    visibleButtonRef.current?.click()
  }

  const scrollToSection = () => {
    const section = document.getElementById(
      stickyCTA.anchor!._id!,
    ) as HTMLElement

    if (section) {
      const offset = 20
      window.scroll({
        top: section.offsetTop - offset,
        behavior: 'smooth',
      })
    }

    const button = document.querySelector('.modal__close-button')
    button?.dispatchEvent(new Event('click'))
  }

  const redirectToUrl = () => {
    window.location.href = stickyCTA.destinationUrl!
  }

  const isStickyVisible =
    (isBuyingPanelOutOfView && stickyCTA?.showStickyCTA) ||
    stickyCTA?.alwaysShow

  useEffect(() => {
    $isStickyCTAVisible.set(!!isStickyVisible)
  }, [isStickyVisible])

  if (!stickyCTA?.showStickyCTA) return null

  const handleAction = () => {
    if (stickyCTA.action === 'anchor') {
      scrollToSection()
    } else if (stickyCTA.action === 'url') {
      redirectToUrl()
    } else {
      addToCart()
    }
  }

  const stickyCTAButtonText =
    stickyCTA.ctaButtonText && stickyCTA.action !== 'cart'
      ? stickyCTA.ctaButtonText
      : stickyCTA.action === 'cart'
        ? 'add to bag'
        : 'sign up now'

  return (
    <div className={`p-sticky-cta-pdp  ${isStickyVisible ? 'is-stuck' : ''}`}>
      <div className="p-sticky-cta-pdp__info-container">
        <div className="p-sticky-cta-pdp__image-container">
          <ResponsiveImage
            document={stickyCTA.image}
            overrides={{
              desktopAspectRatio: '1/1',
              mobileAspectRatio: '1/1',
              loading: 'eager',
              customSizes: [1024],
            }}
          />
        </div>

        <div>
          <p className="type-label p-sticky-cta-pdp__title">{stickyCTA.name}</p>
          <p className="type-body p-sticky-cta-pdp__subtitle">
            {stickyCTA.subtitle}
          </p>
        </div>
      </div>
      <button
        onClick={handleAction}
        className="p-sticky-cta-pdp__action-button button button--primary"
      >
        <p>{stickyCTAButtonText}</p>
        <Icon
          name="primary-arrow-right"
          width={20}
          height={9}
          viewBox="0 0 21 11"
        />
      </button>
    </div>
  )
}
